import React, { createRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { flatten, kebabCase, omit, pick, size } from "lodash";
import ApolloContext from "@tvg/utils/apolloContext";
import { isXSell } from "@tvg/sh-utils/mobileUtils";
import { SeoArea } from "@tvg/design-system";
import Container from "@fdr/static-ui/BaseComponents/Containers";
import ComponentSection from "@fdr/atomic-ui/_organisms/ComponentSection";
import ApolloContextClients from "@tvg/utils/types/ApolloContext";
import HomePageMask from "@tvg/atomic-ui/_static/Masks/homepage";
import { useComponentSorted } from "./hooks/useComponentSorted";
import { useHomepageConfig } from "./hooks/useHomepageConfig";
import {
  getDerbyRace,
  getEnableStoryblokConfig,
  getEventDate,
  getHomepageConfig,
  getPesEventInfo,
  getSeoContentHome,
  getSeoHomeTitle,
  getTodayDate,
  getEnableHomepagePillsNav
} from "./redux/selectors";
import PillsNav from "./components/PillsNav";
import { useCacheConfig } from "./hooks/useCacheConfig";
import useSections from "./hooks/useSections";
import { Props } from "./types";
import { ComponentType } from "./constraints/types";
import { ComponentItem } from "./hooks/types";
import { HomepageConfigType } from "./utils/types";
import { StandaloneWrapper } from "./styled-components";

const HomepageComponent = (props: Props) => {
  const dispatch = useDispatch();
  const homepageCachedConfig = useSelector(getHomepageConfig);
  const enableStoryblokConfig = useSelector(getEnableStoryblokConfig);
  const todayDate = useSelector(getTodayDate);
  const pesEventInfo = useSelector(getPesEventInfo);
  const cachedDerbyRace = useSelector(getDerbyRace);
  const eventDate = useSelector(getEventDate);
  const seoHomeTitle = useSelector(getSeoHomeTitle);
  const seoContentHome = useSelector(getSeoContentHome);
  const enablePillsNav = useSelector(getEnableHomepagePillsNav);

  const homepageConfig = useHomepageConfig(
    homepageCachedConfig,
    enableStoryblokConfig,
    {
      pesEventInfo
    }
  );
  useCacheConfig(homepageConfig, homepageCachedConfig, dispatch);
  const componentSort = useComponentSorted(
    homepageCachedConfig.order,
    dispatch,
    {
      trackAbbr: homepageCachedConfig.eventInfo?.trackAbbr,
      raceNumber: homepageCachedConfig.eventInfo?.raceNumber,
      todayDate,
      eventDate,
      cachedDerbyRace
    }
  );

  const hasComponentLoaded = size(flatten(Object.values(componentSort)));
  const getNighModeStyle = useCallback(
    (componentType: ComponentType) => {
      if (!enableStoryblokConfig) {
        return (
          componentType === ComponentType.FIXED &&
          homepageCachedConfig.currentConfig === HomepageConfigType.DEFAULT
        );
      }

      return componentType === ComponentType.FIXED;
    },
    [enableStoryblokConfig]
  );

  const nonFixedSortedComponents = flatten(
    Object.values(omit(componentSort, [ComponentType.FIXED]))
  );
  const componentsOrder = nonFixedSortedComponents.map((c) => ({
    name: c.name,
    title: c.title
  }));

  const {
    pills,
    handleOnSectionVisibilityChanges,
    isLoading: areSectionsLoading,
    pillsDistanceFromTop
  } = useSections({
    enablePillsNav,
    componentsOrder
  });

  const getSection = useCallback(
    (
      componentsToRender: Array<ComponentItem>,
      context: ApolloContextClients,
      isFixed: boolean
    ) => (
      <Container.HomeGridContainer useStoryblokStyle device={props.device}>
        {componentsToRender.map(
          ({
            name,
            type,
            component: GenericComponent,
            props: customProps = [],
            context: customContext = [],
            title,
            subtitle,
            withoutHeaderSpace,
            isStandalone,
            headerLink,
            withoutBackground
          }) => {
            const sectionRef = createRef<HTMLSpanElement>();
            const handleVisibilityChange = (
              sectionWrapperRef: React.RefObject<HTMLSpanElement>,
              isComponentVisible: boolean
            ) => {
              handleOnSectionVisibilityChanges(
                name,
                title,
                sectionWrapperRef,
                isComponentVisible
              );
            };

            if (isStandalone) {
              return (
                <GenericComponent
                  key={name}
                  customId={name}
                  {...pick({ ...props, title }, customProps)}
                  {...pick(context, customContext)}
                />
              );
            }

            return (
              <StandaloneWrapper ref={sectionRef} key={name}>
                <ComponentSection
                  isNightMode={getNighModeStyle(type)}
                  hasHeaderSpace={!withoutHeaderSpace}
                  withoutBackground={withoutBackground}
                  title={title}
                  subtitle={subtitle}
                  headerLink={headerLink}
                  key={name}
                  qaLabel={kebabCase(`${name}-section`)}
                  onSectionVisibilityChanges={handleVisibilityChange}
                  isFixed={isFixed}
                >
                  <GenericComponent
                    key={name}
                    componentSectionTitle={title}
                    showHeader={false}
                    useNewHomepage
                    withoutBackground={withoutBackground}
                    customId={name}
                    moduleRef={sectionRef}
                    {...pick({ ...props }, customProps)}
                    {...pick(context, customContext)}
                  />
                </ComponentSection>
              </StandaloneWrapper>
            );
          }
        )}
      </Container.HomeGridContainer>
    ),
    [props]
  );

  return (
    <ApolloContext.Consumer>
      {(context: ApolloContextClients) =>
        hasComponentLoaded ? (
          <>
            {enablePillsNav && (
              <PillsNav
                pills={pills}
                isLoading={areSectionsLoading}
                pillsDistanceFromTop={pillsDistanceFromTop}
              />
            )}
            {getSection(componentSort.fixed, context, true)}
            {getSection(nonFixedSortedComponents, context, false)}
            {!isXSell() && (
              <SeoArea title={seoHomeTitle} message={seoContentHome} />
            )}
          </>
        ) : (
          <HomePageMask />
        )
      }
    </ApolloContext.Consumer>
  );
};

export default HomepageComponent;
